const DAY_MS = 86_400_000;

export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function shortId(id: string): string {
  return id.slice(0, 8);
}

/** Whole days between `iso` and `now`; `null` for a missing date. */
export function ageInDays(iso: string | null, now = Date.now()): number | null {
  return iso === null ? null : Math.floor((now - Date.parse(iso)) / DAY_MS);
}

export function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toISOString().replace("T", " ").slice(0, 16);
}

const denseDateMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Compact calendar date in UTC; audit evidence should retain formatDate minutes. */
export function formatDenseDate(iso: string | null): string {
  if (iso === null) return "—";
  const date = new Date(iso);
  return `${date.getUTCDate()} ${denseDateMonths[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export function formatPercent(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

const compactFormatter = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
export function compactNumber(value: number | null): string {
  return value === null ? "—" : compactFormatter.format(value);
}

export function formatAge(iso: string | null, now = Date.now()): string {
  const days = ageInDays(iso, now);
  if (days === null) return "—";
  return days >= 365 ? `${(days / 365.25).toFixed(1)} y` : `${days} d`;
}

export function formatDay(iso: string | null): string {
  return iso === null ? "—" : new Date(iso).toISOString().slice(0, 10);
}

/** Stable colour for a cluster id (golden-angle hue steps over a 32-bit hash). */
export function clusterColor(clusterId: string | null): string {
  if (clusterId === null) return "#6b7280";
  let hash = 0;
  for (const char of clusterId) hash = (Math.imul(hash, 31) + char.charCodeAt(0)) >>> 0;
  // Sigma accepts hex/RGB, not CSS HSL. Preserve the shared 70% / 58% palette.
  const hue = ((hash * 137.508) % 360) / 30;
  const amplitude = 0.7 * Math.min(0.58, 1 - 0.58);
  const channels = [0, 8, 4].map((offset) => {
    const k = (offset + hue) % 12;
    return Math.round(255 * (0.58 - amplitude * Math.max(-1, Math.min(k - 3, 9 - k, 1))))
      .toString(16).padStart(2, "0");
  });
  return `#${channels.join("")}`;
}

/** Saves `blob` as a file through a temporary object URL. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
